import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import API_BASE from '../config';

function SignDelegation() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [step, setStep] = useState('info'); // info | otp | done | already-signed
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [maskedEmail, setMaskedEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [countdown, setCountdown] = useState(600);
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    fetchDelegation();
  }, [token]);

  useEffect(() => {
    if (step === 'otp' && countdown > 0) {
      const timer = setInterval(() => setCountdown(c => c - 1), 1000);
      return () => clearInterval(timer);
    }
  }, [step, countdown]);

  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setInterval(() => setResendCooldown(c => c - 1), 1000);
      return () => clearInterval(timer);
    }
  }, [resendCooldown]);

  const fetchDelegation = async () => {
    try {
      const response = await fetch(`${API_BASE}/sign-delegation/${token}`);
      if (response.status === 400) {
        const d = await response.json();
        if (d.alreadySigned) { setStep('already-signed'); setLoading(false); return; }
      }
      if (!response.ok) { setError('Link non valido o scaduto.'); setLoading(false); return; }
      setData(await response.json());
      setLoading(false);
    } catch (err) { setError('Errore nel caricamento.'); setLoading(false); }
  };

  const handleSendOtp = async () => {
    setSending(true);
    setOtpError('');
    try {
      const response = await fetch(`${API_BASE}/sign-delegation/${token}/send-otp`, { method: 'POST' });
      if (response.ok) {
        const result = await response.json();
        setMaskedEmail(result.maskedEmail);
        setOtpSent(true);
        setStep('otp');
        setCountdown(600);
        setResendCooldown(60);
      } else {
        const d = await response.json();
        setOtpError(d.error || 'Errore nell\'invio del codice.');
      }
    } catch (err) { setOtpError('Errore di connessione.'); }
    setSending(false);
  };

  const handleVerifyOtp = async () => {
    setVerifying(true);
    setOtpError('');
    try {
      const response = await fetch(`${API_BASE}/sign-delegation/${token}/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: otpCode })
      });
      if (response.ok) {
        setStep('done');
      } else {
        const d = await response.json();
        setOtpError(d.error || 'Codice errato.');
      }
    } catch (err) { setOtpError('Errore di connessione.'); }
    setVerifying(false);
  };

  const formatTime = (s) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;

  if (loading) return <div className="container"><p>Caricamento...</p></div>;
  if (error) return <div className="container"><div className="error">{error}</div></div>;

  if (step === 'already-signed') {
    return (
      <div className="container" style={{ textAlign: 'center', padding: '40px 20px' }}>
        <div style={{ fontSize: '48px', marginBottom: '20px' }}>✅</div>
        <h2>Delega già firmata</h2>
        <p style={{ color: '#666' }}>Questa delega è già stata firmata. Non è necessaria alcuna ulteriore azione.</p>
      </div>
    );
  }

  if (step === 'done') {
    return (
      <div className="container" style={{ textAlign: 'center', padding: '40px 20px' }}>
        <div style={{ fontSize: '48px', marginBottom: '20px' }}>✅</div>
        <h2>Delega firmata con successo!</h2>
        <p style={{ color: '#666', marginTop: '10px' }}>
          Hai confermato la responsabilità di vigilanza su <strong>{data.student.firstName} {data.student.lastName}</strong> (Classe {data.student.class}) durante la Festa HEP.
        </p>
      </div>
    );
  }

  if (step === 'otp') {
    return (
      <div className="container" style={{ maxWidth: '400px', margin: '0 auto', padding: '20px' }}>
        <h2 style={{ textAlign: 'center' }}>🔐 Conferma la tua firma</h2>
        <p style={{ textAlign: 'center', color: '#666', fontSize: '14px' }}>
          Un codice di verifica a 6 cifre è stato inviato a <strong>{maskedEmail}</strong>
        </p>

        <div style={{ margin: '20px 0', textAlign: 'center' }}>
          <input
            type="text"
            maxLength={6}
            value={otpCode}
            onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
            placeholder="000000"
            style={{ fontSize: '32px', textAlign: 'center', width: '200px', letterSpacing: '8px', padding: '10px' }}
            autoFocus
          />
        </div>

        {otpError && <div className="error" style={{ textAlign: 'center' }}>{otpError}</div>}

        <div style={{ textAlign: 'center', marginTop: '15px' }}>
          <button className="btn-primary" onClick={handleVerifyOtp} disabled={verifying || otpCode.length !== 6} style={{ width: '100%', maxWidth: '300px' }}>
            {verifying ? 'Verifica in corso...' : 'Conferma'}
          </button>
        </div>

        <div style={{ textAlign: 'center', marginTop: '15px', fontSize: '13px', color: '#888' }}>
          {countdown > 0 ? `Il codice scade tra ${formatTime(countdown)}` : 'Codice scaduto — richiedere un nuovo codice'}
        </div>

        <div style={{ textAlign: 'center', marginTop: '10px' }}>
          <button className="btn-secondary" onClick={handleSendOtp} disabled={resendCooldown > 0 || sending} style={{ fontSize: '13px' }}>
            {resendCooldown > 0 ? `Reinvia codice (${resendCooldown}s)` : '🔄 Reinvia codice'}
          </button>
        </div>
      </div>
    );
  }

  // Info step — show delegation details and ask to sign
  return (
    <div className="container" style={{ maxWidth: '500px', margin: '0 auto', padding: '20px' }}>
      <h2>Autorizzazione alla Delega</h2>

      <div style={{ background: '#f8f9fa', padding: '15px', borderRadius: '8px', marginBottom: '20px' }}>
        <p style={{ marginBottom: '8px' }}><strong>Studente:</strong> {data.student.firstName} {data.student.lastName} (Classe {data.student.class})</p>
        <p style={{ marginBottom: '8px' }}><strong>Genitore delegante:</strong> {data.delegatingParent.firstName} {data.delegatingParent.lastName}</p>
        <p><strong>Genitore delegato (tu):</strong> {data.delegatedParent.firstName} {data.delegatedParent.lastName}</p>
      </div>

      <div style={{ background: '#eafaf1', border: '1px solid #27ae60', padding: '15px', borderRadius: '8px', marginBottom: '20px' }}>
        <p style={{ fontWeight: '600', marginBottom: '8px' }}>Dichiarazione:</p>
        <p style={{ fontSize: '14px' }}>
          Accetto la responsabilità di vigilare sul comportamento di <strong>{data.student.firstName} {data.student.lastName}</strong> durante l'intera durata della Festa HEP, assumendomi ogni responsabilità civile per eventuali danni causati.
        </p>
      </div>

      {otpError && <div className="error">{otpError}</div>}

      <button className="btn-primary" onClick={handleSendOtp} disabled={sending} style={{ width: '100%', padding: '14px', fontSize: '16px' }}>
        {sending ? 'Invio codice...' : '✍️ Firma la delega (riceverai un codice OTP)'}
      </button>

      <p style={{ fontSize: '12px', color: '#888', marginTop: '15px', textAlign: 'center' }}>
        La conferma tramite codice OTP costituisce firma elettronica ai sensi del Reg. UE 910/2014 (eIDAS) e del D.Lgs. 82/2005 (CAD).
      </p>
    </div>
  );
}

export default SignDelegation;
